import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/router'
import {
  ChangeEvent,
  FC,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useToasts } from 'react-toast-notifications'
import { z } from 'zod'
import { motion } from 'framer-motion'

import { FormContainer } from '../FormContainer'
import { Input } from '../Input'
import { Loading } from '../Loading'
import { Select } from '../select'

import { api } from '@services/api'

import { graphNormalize } from '@helpers/graph-normalize'
import { SimulationsApi } from '@services/api/simulations'
import { CalculatorSolarContext } from 'context/CalculatorSolarContext'
import { useWindowDimensions } from '@hooks/use-window-dimensions'
import { HomeButton } from '@components/home/components/home-button'

const preLeadSchema = z.object({
  postalCode: z
    .string({
      required_error: 'Informe o CEP.'
    })
    .min(8, 'CEP inválido.')
    .max(8, 'CEP inválido.'),
  state: z.string().min(2).max(2),
  lightBill: z
    .number({
      required_error: 'Informe o valor da conta de luz.'
    })
    .min(1, 'Valor mínimo de R$ 1,00.'),
  powerDistributor: z.object({
    id: z.number(),
    name: z.string()
  }),
  email: z
    .string({
      required_error: 'Informe o email.'
    })
    .email('Informe um email valido.'),
  city: z.string(),
  addressName: z.string()
})

export type PreLeadFormData = z.infer<typeof preLeadSchema>

interface PowerDistributors {
  id: number
  name: string
}

interface Props {
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p'
}

export const PreLead: FC<Props> = ({ headingLevel }) => {
  const [powerDistributors, setPowerDistributors] = useState<
    PowerDistributors[]
  >([])
  const [isLoadingPowerDistributors, setIsLoadingPowerDistributors] =
    useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { width } = useWindowDimensions()
  const { handleCurrentStep, handleSummaryData, handlePreLeadData } =
    useContext(CalculatorSolarContext)

  const preLeadForm = useForm<PreLeadFormData>({
    resolver: zodResolver(preLeadSchema)
  })
  const {
    control,
    formState: { errors, isSubmitSuccessful },
    getValues,
    handleSubmit,
    clearErrors,
    setError,
    setValue
  } = preLeadForm
  const formValues = getValues()

  const { addToast } = useToasts()
  const router = useRouter()

  async function handlePreLeadSubmit(data: PreLeadFormData) {
    setIsLoading(true)

    try {
      const simulation = await getSimulation({
        postalcode: data.postalCode,
        light_bill: data.lightBill,
        power_distributor_id: data.powerDistributor.id,
        power_distributor_name: data.powerDistributor.name,
        email: data.email
      })

      handlePreLeadData({ ...data, simulationId: simulation.id })

      handleCurrentStep('resumo')

      // Usa asPath para obter o caminho real (não o padrão com parâmetros dinâmicos)
      const currentPath = router.asPath.split('?')[0]
      const queryParams = new URLSearchParams({
        postalcode: data.postalCode,
        light_bill: String(data.lightBill),
        power_distributor_name: data.powerDistributor.name,
        power_distributor_id: String(data.powerDistributor.id),
        email: data.email
      }).toString()

      router.push(`${currentPath}?${queryParams}`, undefined, { scroll: false })
    } catch (err) {
      setIsLoading(false)
    }
  }

  async function fetchPowerDistributors(postalCode: string) {
    try {
      const response = await api.get(
        `/simulations/power_distributors?postalcode=${postalCode}`
      )

      return response.data
    } catch (err) {}
  }

  async function handlePowerDistributor(value: string) {
    const postalCode = value.replace(/\D+/g, '')
    setValue('postalCode', postalCode)

    if (postalCode.length < 8) {
      return
    }
    clearErrors('postalCode')

    setIsLoadingPowerDistributors(true)

    const powerDistributors = await fetchPowerDistributors(postalCode)

    if (powerDistributors.city) {
      setPowerDistributors(powerDistributors.power_distributors)
      setValue('state', powerDistributors.UF)
      setValue('city', powerDistributors.city)
      setValue('addressName', powerDistributors.adress_name)

      clearErrors('state')
    } else {
      setError('postalCode', { message: 'CEP não encontrado' })
      setPowerDistributors([])
      setValue('powerDistributor', undefined)
      setValue('state', '')
      setValue('city', '')
      setValue('addressName', '')
    }

    setIsLoadingPowerDistributors(false)
  }

  function handleSelectChangedValue(data: { label: string; value: number }) {
    if (!data) {
      setValue('powerDistributor', null)
      return
    }

    clearErrors('powerDistributor')

    setValue('powerDistributor', {
      id: data.value,
      name: data.label
    })
  }

  const convertCalculateSimulation = data => {
    return {
      required_power: data.required_power,
      equipment_cost: data.equipment_cost,
      installation_cost: data.installation_cost,
      monthly_production: data.monthly_production,
      freight_value: data.freight_cost,
      simulated_generator_cost_min: data.estimated_generator_cost_min,
      simulated_generator_cost_max: data.estimated_generator_cost_max,
      panel_amount: data.panels_amount,
      maximum_annual_savings: data.annual_savings,
      ...data
    }
  }

  async function getSimulation(value: {
    postalcode: string
    light_bill: number
    power_distributor_id: number
    power_distributor_name: string
    email: string
  }) {
    try {
      const { data } = await api.post('/simulations/calculate', {
        simulation: value
      })
      data.monthly = graphNormalize(data.monthly)
      const simulation = await saveSimulation(
        convertCalculateSimulation({ ...data, ...value })
      )
      handleSummaryData(data)

      return simulation
    } catch (err) {
      addToast('Não foi possível realizar a simulação', {
        autoDismiss: true,
        appearance: 'error'
      })
    }
  }

  const saveSimulation = useCallback(
    async (calculateSimulation: any) => {
      try {
        const response = await SimulationsApi.save({
          light_bill: Number(calculateSimulation.light_bill),
          postalcode: String(calculateSimulation.postalcode),
          power_distributor_code: Number(
            calculateSimulation.power_distributor_id
          ),
          power_distributor_name: String(
            calculateSimulation.power_distributor_name
          ),
          email: String(calculateSimulation.email),
          origin_url:
            localStorage.getItem('origin-url') ||
            top.window.document.location.href,
          ...calculateSimulation
        })

        return response.data
      } catch (error) {
        console.error(error)
      }
    },
    [formValues]
  )

  async function initialPreLeadData(data: {
    postalcode: string
    light_bill: number
    power_distributor_id: number
    power_distributor_name: string
    email: string
  }) {
    setIsLoading(true)

    const simulation = await getSimulation(data)
    const powerDistributors = await fetchPowerDistributors(data.postalcode)

    handlePreLeadData({
      powerDistributor: {
        id: data.power_distributor_id,
        name: data.power_distributor_name
      },
      postalCode: data.postalcode,
      lightBill: data.light_bill,
      email: data.email,
      city: powerDistributors.city,
      state: powerDistributors.UF,
      addressName: powerDistributors.adress_name,
      simulationId: simulation.id
    })

    handleCurrentStep('resumo')
    setIsLoading(false)
  }

  useEffect(() => {
    localStorage.setItem('origin-url', top.window.document.location.href)

    if (
      [
        'postalcode',
        'light_bill',
        'power_distributor_id',
        'power_distributor_name',
        'email'
      ].every(param => param in router.query)
    ) {
      initialPreLeadData({
        postalcode: router.query.postalcode as string,
        light_bill: Number(router.query.light_bill),
        power_distributor_id: Number(router.query.power_distributor_id),
        power_distributor_name: router.query.power_distributor_name as string,
        email: router.query.email as string
      })
    }
  }, [router.query])

  if (isLoading) {
    return (
      <FormContainer headingLevel={headingLevel}>
        <Loading
          loadingMessage={<>PROCESSANDO...</>}
          successMessage={<>PROCESSANDO...</>}
          isSuccessful={isSubmitSuccessful}
          showHouse={false}
        />
      </FormContainer>
    )
  }

  return (
    <motion.div
      whileInView={{
        opacity: 1
      }}
      variants={{
        hidden: {
          opacity: 0
        }
      }}
      initial="hidden"
      transition={{
        duration: 0.3,
        delay: 0.1
      }}
      viewport={{
        once: true
      }}
      className="w-full h-fit mb-2"
    >
      <FormContainer headingLevel={headingLevel}>
        {/* @ts-expect-error - FormProvider className prop incompatível com tipos react-hook-form */}
        <FormProvider {...preLeadForm} className="h-fit">
          <form
            className="flex flex-col justify-between md:gap-[30px] h-fit xl:items-center"
            onSubmit={handleSubmit(handlePreLeadSubmit)}
          >
            <div className="w-full flex flex-col gap-y-4 1.5xl:gap-y-5">
              <div className="flex gap-3">
                <Input
                  name="CEP"
                  variant="mask"
                  mask="99999-999"
                  labelText="CEP"
                  placeholder="Digite seu CEP"
                  className="flex-[1_1_200px] lg:flex-[1_1_150px] xl:flex-[1_1_168px]"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    handlePowerDistributor(event.target.value)
                  }
                  isError={!!errors.postalCode}
                  errorMessage={errors.postalCode?.message}
                  disabled={isLoadingPowerDistributors}
                />

                <Input
                  name="state"
                  labelText="UF"
                  placeholder="UF"
                  className="flex-[0_1_73px] lg:flex-[0_1_112px] xl:flex-[1_1_52px]"
                  isError={!!errors.state}
                  disabled
                />
              </div>

              <Controller
                control={control}
                name="lightBill"
                render={({ field: { onChange, ref, ...rest } }) => (
                  <Input
                    variant="currency"
                    maxCurrency={1000000000}
                    labelText="Valor da conta de luz"
                    placeholder="R$ 00,00"
                    onChange={(_, value) => onChange(value)}
                    isError={!!errors.lightBill}
                    errorMessage={errors.lightBill?.message}
                    className="w-full"
                    {...rest}
                  />
                )}
                rules={{ required: true }}
              />

              <Controller
                control={control}
                name="powerDistributor"
                render={({ field: { onChange, ref, value, ...rest } }) => (
                  <Select
                    className="w-full"
                    labelText="Concessionária"
                    placeholder="Digite sua concessionária"
                    onChange={handleSelectChangedValue}
                    isError={!!errors.powerDistributor}
                    maxMenuHeight={200}
                    value={
                      value?.id && {
                        label: value.name,
                        value: value.id
                      }
                    }
                    options={powerDistributors}
                    isSearchable={width >= 1024}
                    {...rest}
                  />
                )}
                rules={{ required: true }}
              />

              <Input
                name="email"
                labelText="E-mail"
                placeholder="Digite seu email"
                className="w-full"
                isError={!!errors.email}
                errorMessage={errors.email?.message}
              />
            </div>

            <div className="w-full my-4">
              <HomeButton variant="primary" type="submit">
                SIMULE GRÁTIS!
              </HomeButton>
            </div>
          </form>
        </FormProvider>
      </FormContainer>
    </motion.div>
  )
}
